import type { ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import type { RustyPiece } from '@/lib/types';
import { relativeDate } from '@/lib/relative-date';
import { classifyRusty, type RustySeverity } from '@/lib/rusty';

type Props = {
  pieces: RustyPiece[];
};

const SEVERITY_TO_BORDER: Readonly<Record<RustySeverity, string>> = {
  never: 'border-l-gray-300',
  severe: 'border-l-red-500',
  warning: 'border-l-amber-500',
  mild: 'border-l-yellow-500',
  fresh: 'border-l-gray-300',
};

function severityBorderColor(days: number): string {
  return SEVERITY_TO_BORDER[classifyRusty(days)];
}

export function RustyPiecesList({ pieces }: Props): ReactElement {
  if (pieces.length === 0) {
    return <p className="text-sm text-[var(--ink-3)]">All caught up! No rusty pieces.</p>;
  }

  return (
    <ul className="space-y-2">
      {pieces.map((piece) => (
        <li key={piece.userArrangementId}>
          <Link
            to="/repertoire/$arrangementId"
            params={{ arrangementId: piece.arrangementId }}
            className={`block border-l-4 ${severityBorderColor(piece.daysSinceLastPracticed)} rounded-r-lg p-3 bg-[var(--bg-surface)] hover:bg-[var(--bg-soft)] transition-colors`}
          >
            <p className="font-medium text-sm">{piece.pieceName}</p>
            <p className="text-xs text-[var(--ink-3)]">{piece.arrangementName}</p>
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-[var(--ink-3)]">
                {relativeDate(piece.daysSinceLastPracticed)}
              </span>
              {piece.masteryLevelName && (
                <span className="text-xs text-[var(--ink-3)]">{piece.masteryLevelName}</span>
              )}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
